/**
 * На iOS ::-webkit-scrollbar не поддерживается, скроллбар показывается только при скролле.
 * Директива добавляет всегда видимый кастомный скроллбар для указанных элементов (только на iOS).
 *
 * Использование:
 * - v-ios-scrollbar — элемент с директивой является скроллируемым контейнером
 * - v-ios-scrollbar="'.popup__window'" — найти все .popup__window внутри и добавить скроллбар
 * - v-ios-scrollbar="['.popup__window', '.dropdown__list-scroll']" — несколько селекторов
 */

const SCROLLBAR_WIDTH = 4;
const THUMB_MIN_HEIGHT = 40;

function shouldEnableCustomScrollbar() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  // iOS custom JS scrollbar causes visible scroll jank in this project on fast direction changes.
  // Keep native iOS scrolling for smoothness.
  return false;
}

function hasOverlayScrollbars(el) {
  if (!el || !el.querySelector) return false;
  if (el.hasAttribute('data-overlayscrollbars')) return true;
  if (el.matches('.os-host')) return true;
  if (el.querySelector('[data-overlayscrollbars-viewport]')) return true;
  if (el.querySelector('[data-overlayscrollbars-scrollbar="vertical"]')) return true;
  return false;
}

function createScrollbar(el, theme = 'default') {
  const computedStyle = getComputedStyle(el);
  const originalPosition = el.style.position;
  if (!originalPosition && computedStyle.position === 'static') {
    el.style.position = 'relative';
  }

  const track = document.createElement('div');
  track.className = `ios-scrollbar-track ios-scrollbar-track--${theme}`;
  track.style.position = 'absolute';
  track.style.top = '0';
  track.style.right = '0';
  track.style.left = 'auto';
  track.style.width = `${SCROLLBAR_WIDTH}px`;
  track.style.height = '100%';
  track.style.pointerEvents = 'none';
  const thumb = document.createElement('div');
  thumb.className = `ios-scrollbar-thumb ios-scrollbar-thumb--${theme}`;
  thumb.style.pointerEvents = 'auto';
  track.appendChild(thumb);
  el.appendChild(track);

  let rafId = null;
  let touchStartY = 0;
  let touchStartScrollTop = 0;

  function updateThumb() {
    if (!el.isConnected || el.offsetParent === null) {
      track.style.opacity = '0';
      return;
    }

    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;

    if (scrollHeight <= clientHeight) {
      track.style.opacity = '0';
      return;
    }
    track.style.opacity = '1';
    const trackHeight = clientHeight;
    const ratio = clientHeight / scrollHeight;
    let thumbHeight = Math.max(THUMB_MIN_HEIGHT, Math.round(trackHeight * ratio));
    if (thumbHeight >= trackHeight) thumbHeight = trackHeight;
    const maxScroll = scrollHeight - clientHeight;
    const scrollTop = el.scrollTop;
    const thumbMaxTop = trackHeight - thumbHeight;
    const thumbTop = maxScroll > 0 ? (scrollTop / maxScroll) * thumbMaxTop : 0;

    // Компенсируем прокрутку контейнера, чтобы трек оставался в видимой области блока.
    track.style.transform = `translateY(${scrollTop}px)`;
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbTop}px)`;
  }

  function scheduleUpdate() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      updateThumb();
    });
  }

  el.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate, { passive: true });

  const resizeObserver = new ResizeObserver(scheduleUpdate);
  resizeObserver.observe(el);

  function onThumbTouchStart(e) {
    e.preventDefault();
    touchStartY = e.touches[0].clientY;
    touchStartScrollTop = el.scrollTop;
    document.addEventListener('touchmove', onThumbTouchMove, { passive: false });
    document.addEventListener('touchend', onThumbTouchEnd);
  }

  function onThumbTouchMove(e) {
    e.preventDefault();
    const deltaY = e.touches[0].clientY - touchStartY;
    const trackHeight = track.clientHeight;
    const thumbHeight = thumb.clientHeight;
    const thumbMaxTop = trackHeight - thumbHeight;
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (maxScroll <= 0 || thumbMaxTop <= 0) return;
    const scrollDelta = (deltaY / thumbMaxTop) * maxScroll;
    el.scrollTop = Math.max(0, Math.min(maxScroll, touchStartScrollTop + scrollDelta));
  }

  function onThumbTouchEnd() {
    document.removeEventListener('touchmove', onThumbTouchMove);
    document.removeEventListener('touchend', onThumbTouchEnd);
  }

  thumb.addEventListener('touchstart', onThumbTouchStart, { passive: false });

  // Скрываем нативный скроллбар, чтобы не было двух полос (кастомная + системная)
  el.classList.add('has-ios-scrollbar');

  updateThumb();

  return {
    destroy() {
      el.classList.remove('has-ios-scrollbar');
      if (!originalPosition && computedStyle.position === 'static') {
        el.style.position = '';
      }
      el.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver.disconnect();
      thumb.removeEventListener('touchstart', onThumbTouchStart);
      document.removeEventListener('touchmove', onThumbTouchMove);
      document.removeEventListener('touchend', onThumbTouchEnd);
      if (rafId !== null) cancelAnimationFrame(rafId);
      track.remove();
    },
  };
}

function initScrollbarForElement(el, theme = 'default') {
  const overflowY = getComputedStyle(el).overflowY;
  const canScroll = overflowY === 'auto' || overflowY === 'scroll';
  const hasOverlay = hasOverlayScrollbars(el);

  if (hasOverlay) {
    destroyScrollbarForElement(el);
    ensureOverlayWatcher(el, theme);
    return;
  }

  if (canScroll && !el._iosScrollbarInstance) {
    el._iosScrollbarInstance = createScrollbar(el, theme);
  }

  ensureOverlayWatcher(el, theme);
}

function destroyScrollbarForElement(el) {
  if (el._iosScrollbarInstance) {
    el._iosScrollbarInstance.destroy();
    delete el._iosScrollbarInstance;
  }
}

function ensureOverlayWatcher(el, theme = 'default') {
  if (el._iosOverlayWatcher) return;

  const checkState = () => {
    if (!el || !el.isConnected) return;

    const overflowY = getComputedStyle(el).overflowY;
    const canScroll = overflowY === 'auto' || overflowY === 'scroll';
    const hasOverlay = hasOverlayScrollbars(el);

    if (hasOverlay) {
      destroyScrollbarForElement(el);
      return;
    }

    if (canScroll && !el._iosScrollbarInstance) {
      el._iosScrollbarInstance = createScrollbar(el, theme);
    }
  };

  const observer = new MutationObserver(checkState);
  observer.observe(el, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'data-overlayscrollbars'],
  });

  el._iosOverlayWatcher = observer;
  el._iosOverlayWatcherCheck = checkState;
}

function destroyOverlayWatcher(el) {
  if (el._iosOverlayWatcher) {
    el._iosOverlayWatcher.disconnect();
    delete el._iosOverlayWatcher;
  }
  if (el._iosOverlayWatcherCheck) {
    delete el._iosOverlayWatcherCheck;
  }
}

function getSelectors(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return [value];
  if (value && value.selectors) return value.selectors;
  return null;
}

function getTheme(value) {
  if (value && value.theme) return value.theme;
  return 'default';
}

function getSafeSelectors(selectors) {
  if (!Array.isArray(selectors)) return [];
  return selectors.filter((selector) => typeof selector === 'string' && selector.trim().length > 0);
}

function initForNode(node, selectors, theme) {
  if (!node || node.nodeType !== 1 || !selectors.length) return;

  selectors.forEach((selector) => {
    try {
      if (node.matches && node.matches(selector)) {
        initScrollbarForElement(node, theme);
      }
      if (node.querySelectorAll) {
        node.querySelectorAll(selector).forEach((target) => {
          initScrollbarForElement(target, theme);
        });
      }
    } catch (e) {
      // invalid selector
    }
  });
}

function destroyForNode(node, selectors) {
  if (!node || node.nodeType !== 1 || !selectors.length) return;

  selectors.forEach((selector) => {
    try {
      if (node.matches && node.matches(selector)) {
        destroyScrollbarForElement(node);
        destroyOverlayWatcher(node);
      }
      if (node.querySelectorAll) {
        node.querySelectorAll(selector).forEach((target) => {
          destroyScrollbarForElement(target);
          destroyOverlayWatcher(target);
        });
      }
    } catch (e) {
      // invalid selector
    }
  });
}

export default {
  mounted(el, binding) {
    if (!shouldEnableCustomScrollbar()) return;

    const selectors = getSafeSelectors(getSelectors(binding.value));
    const theme = getTheme(binding.value);

    if (selectors && selectors.length > 0) {
      let initRafId = null;
      let pendingNodes = [];
      const initAll = () => {
        initForNode(el, selectors, theme);
      };
      const scheduleInitForNodes = () => {
        if (initRafId !== null) return;
        initRafId = requestAnimationFrame(() => {
          initRafId = null;
          const nodes = pendingNodes;
          pendingNodes = [];
          if (!nodes.length) return;
          nodes.forEach((node) => initForNode(node, selectors, theme));
        });
      };

      initAll();

      const observer = new MutationObserver((mutations) => {
        let hasAddedElements = false;

        mutations.forEach((m) => {
          m.removedNodes.forEach((node) => {
            destroyForNode(node, selectors);
          });

          m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            hasAddedElements = true;
            pendingNodes.push(node);
          });
        });

        if (hasAddedElements) {
          scheduleInitForNodes();
        }
      });
      observer.observe(el, { childList: true, subtree: true });

      el._iosScrollbarObserver = observer;
      el._iosScrollbarBinding = binding.value;
      el._iosScrollbarInitRafCleanup = () => {
        if (initRafId !== null) {
          cancelAnimationFrame(initRafId);
          initRafId = null;
        }
      };
    } else {
      initScrollbarForElement(el, theme);
    }
  },

  unmounted(el) {
    if (el._iosScrollbarObserver) {
      el._iosScrollbarObserver.disconnect();
      delete el._iosScrollbarObserver;
    }
    if (el._iosScrollbarInitRafCleanup) {
      el._iosScrollbarInitRafCleanup();
      delete el._iosScrollbarInitRafCleanup;
    }
    const selectors = getSafeSelectors(getSelectors(el._iosScrollbarBinding));
    if (selectors && selectors.length > 0) {
      destroyForNode(el, selectors);
    }
    destroyScrollbarForElement(el);
    destroyOverlayWatcher(el);
    delete el._iosScrollbarBinding;
  },
};
